"use client";

import { authFetch } from "@/lib/authFetch";
import Link from "next/link";
import { Button } from "@project-x/ui-kit";
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { recordEventPageView } from "@/lib/eventViewBeacon";
import { useAnalyticsConsent } from "@/lib/analyticsConsentContext";
import { useSearchParams } from "next/navigation";
import type { TicketTypeDetail } from "@project-x/api-clients";
import { formatSocialLinkDisplayValue, type SocialLinkKind } from "@project-x/api-clients";

import { TicketSelectionClient, type TicketDisplayOption } from "./TicketSelectionClient";
import { FriendTicketCount } from "@/components/FriendTicketCount";
import { EmbeddedEventMap, buildVenueMapsUrl } from "@/components/EmbeddedEventMap";
import { EventMetaRow, type EventMetaItem } from "@/components/events/EventMetaRow";
import { CollapsibleDescription } from "@/components/events/CollapsibleDescription";
import { useAuth } from "@/app/auth/AuthSessionProvider";
import type { LoadedEvent } from "./eventTypes";
import { formatEventDateShort, formatEventDateTimeLong, formatEventTime24 } from "@/utils/timezone";
import { isFreeClaimTicket, isPayAtDoorTicket, isZeroPaymentClaimTicket, remainingClaimableTickets } from "@/utils/ticketTypeAttributes";
import { useLocale, useTranslations } from "next-intl";
import {
  applyPromoValidationResult,
  resolvePromoCodeForClient,
} from "@/lib/promoAttribution";


/**
 * Builds the street line shown under "Ubicación: Venue". Mirrors mobile: the
 * street address only (city already lives in the meta row); falls back to
 * "city, state" when the venue has no street so the block never reads empty.
 * Returns null when there is no venue or nothing to show.
 */
export const formatAddressLine = (event: LoadedEvent | null): string | null => {
  if (!event?.venue) return null;
  const v = event.venue;
  const street = v.address_line1?.trim();
  if (street) return street;
  const parts = [v.city, v.state].filter(
    (part): part is string => Boolean(part && String(part).trim().length > 0),
  );
  if (parts.length === 0) return null;
  return parts.join(", ");
};

/** True when the venue carries usable GPS coordinates (Google Maps enabled in Manager). */
const hasVenueCoordinates = (event: LoadedEvent | null): event is LoadedEvent & {
  venue: NonNullable<LoadedEvent["venue"]> & { google_latitude: number; google_longitude: number };
} => {
  const lat = event?.venue?.google_latitude;
  const lng = event?.venue?.google_longitude;
  return typeof lat === "number" && typeof lng === "number" && Number.isFinite(lat) && Number.isFinite(lng);
};

/** Instagram icon for social link blocks (matches existing info-block stroke style). */
const InstagramLinkIcon = () => (
  <svg className="gruv-event__info-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="2.5" y="2.5" width="15" height="15" rx="4" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="14.5" cy="5.5" r="0.75" fill="currentColor" />
  </svg>
);

/** Twitter/X icon for social link blocks. */
const TwitterLinkIcon = () => (
  <svg className="gruv-event__info-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3 3L17 17M17 3L3 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** Website/globe icon for social link blocks. */
const WebsiteLinkIcon = () => (
  <svg className="gruv-event__info-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2.5 10H17.5M10 2.5C12 5 12 15 10 17.5M10 2.5C8 5 8 15 10 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** Small external-link arrow used by social chips and artist cards. */
const ExternalArrowIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
);

/** Spotify-style play glyph for the lineup CTA (brand-neutral, no trademark asset). */
const SpotifyGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M7.5 9.2c3.2-.9 6.6-.6 9.3.9M8 12.2c2.6-.7 5.3-.4 7.5.8M8.5 15c2-.5 4-.3 5.7.6" />
  </svg>
);

type SocialLinkItem = {
  key: SocialLinkKind;
  href: string;
  label: string;
  displayValue: string;
  icon: ReactNode;
};

/**
 * Backoff schedule for transient availability read failures. A single failed
 * read makes every ticket unselectable for authenticated users, so recover
 * without a manual refresh — but keep the budget tight now that MGET is fast.
 */
const STOCK_RETRY_DELAYS_MS = [200, 500, 1000];

/**
 * Platform policy: a purchase is never unlimited. Managers may configure their
 * own per_user_limit; when a ticket type has none, this default applies.
 *
 * Must mirror platformDefaultPerUserLimit in the orders service — otherwise the
 * UI offers quantities checkout will reject with "purchase limit exceeded".
 */
const PLATFORM_DEFAULT_PER_USER_LIMIT = 10;

/**
 * Checks if a ticket is classic FREE (1-claim, GRATIS). PAY_AT_DOOR is not.
 */
const isFreeTicket = (ticket: TicketTypeDetail): boolean => {
  return isFreeClaimTicket(ticket.attributes ?? null);
};

/**
 * Formats a ticket price for display with symbol only (no currency code).
 * E.g. 10000 → "$ 100.00". Uses pricing.consumer_total when present, otherwise
 * face value + fees.
 * FREE keeps "GRATIS". PAY_AT_DOOR shows "$ 0" without classification copy.
 */
const formatPriceLabel = (ticket: TicketTypeDetail): string | null => {
  if (isFreeTicket(ticket)) {
    return "GRATIS";
  }
  if (isPayAtDoorTicket(ticket.attributes ?? null)) {
    return "$ 0";
  }
  const amount =
    ticket.pricing?.consumer_total ?? ticket.face_value + ticket.service_fee + ticket.platform_fee;
  const value = amount / 100;
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
  return `$ ${formatted}`;
};

/**
 * Derives a combo ratio label (e.g. "4x1") from ticket attributes when type is COMBO.
 * Returns null for non-COMBO or when grant/pay amounts are missing.
 * Exported for unit tests.
 */
export function getComboLabel(attributes?: Record<string, unknown> | null): string | null {
  if (!attributes || attributes.type !== "COMBO") return null;
  const grant = attributes.comboGrantAmount;
  const pay = attributes.comboPayAmount;
  if (grant == null || pay == null) return null;
  const g = Number(grant);
  const p = Number(pay);
  if (!Number.isFinite(g) || !Number.isFinite(p) || g < 1 || p < 1) return null;
  return `${g}x${p}`;
}

/**
 * Normalizes a raw API TicketTypeDetail into the UI-friendly TicketDisplayOption
 * used by the ticket selector sidebar.
 *
 * - Computes the final price in cents and human label.
 * - Derives selectable remaining as min(per-user leftover, Redis stock remaining).
 * - Fail-closed when stockCheckEnabled: Redis stock unknown (-1), missing, or not
 *   yet loaded → remaining 0 so the UI cannot offer quantity checkout will reject.
 * - When stockCheckEnabled is false (logged-out visitors), catalogue status drives
 *   sellout and stockUnknown stays false — no Redis gate for anonymous users.
 * - Ensures the limits object is always defined so the consumer code can rely on it.
 * - Sets comboLabel (e.g. "4x1") when ticket type is COMBO from attributes.
 * - Sets isFree flag for FREE ticket types.
 * - When ticket.valid_until differs from event end, formatValidTo(ticket.valid_until) is used as validToLabel.
 */
export const toDisplayOption = (
  ticket: TicketTypeDetail,
  imageUrl?: string | null,
  ticketsAlreadyOwned: number = 0,
  event?: { ends_at?: string | null; timezone?: string } | null,
  formatValidTo?: (validUntilIso: string) => string,
  stockRemaining?: number | null,
  stockAvailabilityLoaded: boolean = false,
  stockCheckEnabled: boolean = true,
  stockCheckFailed: boolean = false
): TicketDisplayOption => {
  const amount =
    ticket.pricing?.consumer_total ?? ticket.face_value + ticket.service_fee + ticket.platform_fee;

  const perUserLimit = ticket.per_user_limit ?? PLATFORM_DEFAULT_PER_USER_LIMIT;
  const perUserRemaining = Math.max(0, perUserLimit - ticketsAlreadyOwned);

  const soldOutByStatus = ticket.status === "SOLD_OUT" || ticket.quota === 0;
  const stockKnown =
    stockCheckEnabled &&
    stockAvailabilityLoaded &&
    stockRemaining !== null &&
    stockRemaining !== undefined &&
    stockRemaining >= 0;

  let remaining: number;
  if (soldOutByStatus) {
    remaining = 0;
  } else if (!stockCheckEnabled) {
    // Logged-out: catalogue only — selectors stay disabled by auth, no Redis gate.
    remaining = perUserRemaining;
  } else if (stockKnown) {
    remaining = Math.min(perUserRemaining, stockRemaining as number);
  } else {
    // Redis unknown (-1), missing, or fetch not finished: fail closed so the
    // consumer cannot select tickets that checkout will reject with 409.
    remaining = 0;
  }

  const limits: TicketDisplayOption['limits'] = {
    minPerOrder: ticket.min_per_order ?? undefined,
    maxPerOrder: ticket.max_per_order ?? undefined,
    // Configured cap, or the platform default when the ticket type has none.
    // Never the per-user leftover: this value doubles as the absolute cap.
    perUserLimit: soldOutByStatus ? undefined : perUserLimit,
    // Leftover for this user; 0 is the only proof that the personal cap is hit.
    perUserRemaining: soldOutByStatus ? undefined : perUserRemaining,
    remaining,
    // Distinguishes "stock unreadable" from a genuine sellout so the UI copy and
    // the retry loop stay honest during an orders/Redis outage.
    stockUnknown: stockCheckEnabled && !soldOutByStatus && !stockKnown,
    stockCheckFailed: stockCheckEnabled && !soldOutByStatus && !stockKnown && stockCheckFailed,
  };

  const comboLabel = getComboLabel(ticket.attributes ?? null);
  const ticketIsFree = isFreeTicket(ticket);
  const ticketIsPayAtDoor = isPayAtDoorTicket(ticket.attributes ?? null);

  const validToLabel =
    event?.ends_at &&
    ticket.valid_until &&
    ticket.valid_until !== event.ends_at &&
    formatValidTo
      ? formatValidTo(ticket.valid_until)
      : undefined;

  return {
    id: ticket.id,
    name: ticket.name,
    description: ticket.description,
    priceLabel: formatPriceLabel(ticket),
    priceInCents: ticketIsFree || ticketIsPayAtDoor ? 0 : amount,
    limits,
    imageUrl: imageUrl ?? null,
    comboLabel: comboLabel ?? undefined,
    validToLabel: validToLabel ?? undefined,
    isFree: ticketIsFree,
    isPayAtDoor: ticketIsPayAtDoor,
  };
};

interface EventPageClientProps {
  slug: string;
  initialEvent: LoadedEvent;
  initialHeroImageUrl: string | null;
}

/**
 * Client component for the event detail page.
 * Receives server-fetched event data as props so the page renders fully on first paint.
 * Only client-side concerns (ticket counts, promoter validation) are fetched in effects.
 */
export const EventPageClient = ({
  slug,
  initialEvent,
  initialHeroImageUrl,
}: EventPageClientProps) => {
  const t = useTranslations("events");
  const tCommon = useTranslations("common");
  const [userTicketCounts, setUserTicketCounts] = useState<Record<string, number>>({});
  const [freeAvailability, setFreeAvailability] = useState<Record<string, number>>({});
  /** True after the credit-hold availability fetch completes (success or empty). */
  const [freeAvailabilityLoaded, setFreeAvailabilityLoaded] = useState(false);
  /** Redis stock remaining per ticket type from orders availability API. */
  const [stockAvailability, setStockAvailability] = useState<Record<string, number>>({});
  /** True after the stock availability fetch completes (success or empty map). */
  const [stockAvailabilityLoaded, setStockAvailabilityLoaded] = useState(false);
  /** True when availability retries are exhausted for the current auth session. */
  const [stockCheckFailed, setStockCheckFailed] = useState(false);
  /** Bumps to force a manual stock refetch after a failed check. */
  const [stockRetryToken, setStockRetryToken] = useState(0);
  const { isAuthenticated, isLoading } = useAuth();
  const searchParams = useSearchParams();
  const promoterCode = searchParams.get("promoter");
  const { accepted: analyticsAccepted } = useAnalyticsConsent();

  const retryStockAvailability = () => {
    setStockCheckFailed(false);
    setStockAvailabilityLoaded(false);
    setStockRetryToken((token) => token + 1);
  };

  const event = initialEvent;
  const heroImageUrl = initialHeroImageUrl;

  // Effect to handle promoter codes from the URL, validating and storing them in a cookie.
  useEffect(() => {
    const handlePromoterCode = async () => {
      if (!promoterCode) {
        const cookieCode = resolvePromoCodeForClient();
        if (cookieCode && event) {
          await validateAndStorePromoterCode(cookieCode, event.id);
          return;
        }
        return;
      }

      if (event) {
        await validateAndStorePromoterCode(promoterCode, event.id);
      }
    };

    if (!isLoading && event) {
      handlePromoterCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoterCode, isLoading, event?.id]);

  const search = searchParams.toString();
  useEffect(() => {
    if (!event?.id || !analyticsAccepted) return;
    const status = event.status?.toUpperCase();
    if (status === "DRAFT" || status === "CANCELED" || status === "ARCHIVED") return;
    recordEventPageView({
      eventId: event.id,
      publicSlug: event.public_slug || slug,
      search: search ? `?${search}` : "",
      promoterCode: promoterCode,
    });
  }, [event?.id, event?.public_slug, event?.status, slug, search, promoterCode, analyticsAccepted]);

  /**
   * Validates a promoter code via /api/promoters/validate for the given event.
   * Uses shared promoAttribution helpers so cookie/localStorage/clear semantics
   * stay consistent with TicketSelectionClient.
   */
  const validateAndStorePromoterCode = async (code: string, eventId: string) => {
    try {
      const response = await authFetch(
        `/api/promoters/validate?code=${encodeURIComponent(code)}&eventId=${eventId}`,
      );
      if (response.ok) {
        const data = await response.json();
        applyPromoValidationResult({
          code,
          eventId,
          ok: true,
          status: response.status,
          valid: Boolean(data.valid),
          responseEventId: data.eventId,
        });
        return;
      }
      applyPromoValidationResult({
        code,
        eventId,
        ok: false,
        status: response.status,
      });
    } catch {
      // Network / timeout: retain existing attribution.
    }
  };

  // Owned counts come from the authenticated availability response
  // (ownedByTicketType). Clear them when the user logs out.
  useEffect(() => {
    if (!isAuthenticated) {
      setUserTicketCounts({});
    }
  }, [isAuthenticated]);

  // Fetch remaining credit-hold quantities for FREE ticket types (safety net).
  // Auth-gated: anonymous visitors must not trigger free-availability compute.
  useEffect(() => {
    if (!event || isLoading || !isAuthenticated) {
      setFreeAvailability({});
      setFreeAvailabilityLoaded(false);
      return;
    }
    const hasFreeTickets = event.ticket_types.some((tt) =>
      isZeroPaymentClaimTicket(tt.attributes ?? null)
    );
    if (!hasFreeTickets) {
      setFreeAvailability({});
      setFreeAvailabilityLoaded(false);
      return;
    }

    let cancelled = false;
    setFreeAvailability({});
    setFreeAvailabilityLoaded(false);

    const fetchFreeAvailability = async () => {
      try {
        const res = await authFetch(`/api/events/${event.id}/free-availability`, {
          cache: "no-store",
        });
        if (!res.ok || cancelled) {
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const item of data.items || []) {
          map[item.ticketTypeId] = item.remaining;
        }
        setFreeAvailability(map);
        setFreeAvailabilityLoaded(true);
      } catch {
        // Best-effort; on failure leave freeAvailabilityLoaded=false so
        // the override logic does not trigger — tickets keep their backend status.
      }
    };

    void fetchFreeAvailability();

    const handleFocus = () => {
      void fetchFreeAvailability();
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", handleFocus);
    };
  }, [event?.id, isAuthenticated, isLoading]);

  // Fetch real-time stock remaining from orders availability counters.
  // Auth-gated: logged-out visitors see catalogue prices + login CTA only.
  useEffect(() => {
    if (!event?.id || !event.ticket_types?.length) return;
    if (isLoading || !isAuthenticated) {
      setStockAvailability({});
      setStockAvailabilityLoaded(false);
      setStockCheckFailed(false);
      return;
    }

    setStockAvailabilityLoaded(false);
    setStockCheckFailed(false);

    const ids = event.ticket_types
      .filter((tt) => tt.status === "ACTIVE" || tt.status === "SOLD_OUT")
      .map((tt) => tt.id);
    if (ids.length === 0) {
      setStockAvailability({});
      setStockAvailabilityLoaded(true);
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const fetchStock = async (attempt = 0): Promise<void> => {
      if (cancelled) return;
      try {
        const qs = encodeURIComponent(ids.join(","));
        const res = await authFetch(
          `/api/events/${encodeURIComponent(event.id)}/availability?ticketTypeIds=${qs}`,
          { cache: "no-store" }
        );
        if (!res.ok) {
          throw new Error(`availability responded ${res.status}`);
        }
        const data = await res.json();
        const map: Record<string, number> = {};
        for (const item of data.ticketTypes || []) {
          if (item.ticketTypeId) {
            map[item.ticketTypeId] = Number(item.remaining);
          }
        }
        // A 200 that omits requested ticket types leaves them unselectable with
        // no error anywhere; surface it instead of silently failing closed.
        const missing = ids.filter((id) => !(id in map));
        if (missing.length > 0) {
          console.error("[EventPage] availability response omitted ticket types", {
            eventId: event.id,
            requested: ids.length,
            missing,
          });
        }
        // Unresolved counters (-1) or omitted types: fail closed with retry CTA,
        // not a permanent "Verificando…" state.
        const unresolved = ids.filter((id) => {
          const remaining = map[id];
          return remaining === undefined || remaining === null || Number(remaining) < 0;
        });
        if (cancelled) return;
        setStockAvailability(map);
        setStockAvailabilityLoaded(true);
        setStockCheckFailed(unresolved.length > 0);
        // Prefer owned counts from availability so we avoid a full GET /api/orders.
        // Always apply when present (including {}) so a later refetch clears stale owned.
        if (data.ownedByTicketType && typeof data.ownedByTicketType === "object") {
          const owned: Record<string, number> = {};
          for (const [ttId, qty] of Object.entries(data.ownedByTicketType)) {
            owned[ttId] = Number(qty) || 0;
          }
          setUserTicketCounts(owned);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("[EventPage] availability read failed", {
          eventId: event.id,
          attempt,
          error: err instanceof Error ? err.message : String(err),
        });
        const delay = STOCK_RETRY_DELAYS_MS[attempt];
        if (delay !== undefined) {
          retryTimer = setTimeout(() => void fetchStock(attempt + 1), delay);
          return;
        }
        // Out of retries: fail closed (remaining=0). limits.stockUnknown keeps
        // the UI from claiming a sellout it never confirmed.
        console.error("[EventPage] availability unavailable after retries", { eventId: event.id });
        setStockAvailability({});
        setStockAvailabilityLoaded(true);
        setStockCheckFailed(true);
      }
    };

    void fetchStock();
    const handleFocus = () => void fetchStock();
    window.addEventListener("focus", handleFocus);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener("focus", handleFocus);
    };
  }, [event?.id, event?.ticket_types, isAuthenticated, isLoading, stockRetryToken]);

  // Compact meta row under the title (date · time · productora · city · age),
  // mirroring the mobile event detail.
  const locale = useLocale();
  const metaItems = useMemo((): EventMetaItem[] => {
    if (!event) return [];
    const lang = locale === "en" ? "en" : "es";
    const items: EventMetaItem[] = [
      { key: "date", label: t("detail.meta.date"), value: formatEventDateShort(event.starts_at, event.timezone, lang) },
      { key: "time", label: t("detail.meta.time"), value: formatEventTime24(event.starts_at, event.timezone) },
    ];
    if (event.productora_name?.trim()) {
      items.push({ key: "productora", label: t("detail.meta.productora"), value: event.productora_name.trim() });
    }
    if (event.venue?.city?.trim()) {
      items.push({ key: "city", label: t("detail.meta.city"), value: event.venue.city.trim() });
    }
    if (event.min_age != null && event.min_age > 0) {
      items.push({ key: "age", label: t("detail.meta.age"), value: `+${event.min_age}` });
    }
    return items;
  }, [event, locale, t]);
  const addressLine = useMemo(() => formatAddressLine(event), [event]);
  const venueName = event?.venue?.name?.trim() || null;
  const venueMapsUrl = useMemo(() => {
    if (!event?.venue) return null;
    if (hasVenueCoordinates(event)) {
      return buildVenueMapsUrl(event.venue.google_latitude, event.venue.google_longitude);
    }
    const query = encodeURIComponent([venueName, event.venue.city].filter(Boolean).join(" ").trim());
    return query ? `https://www.google.com/maps/search/?api=1&query=${query}` : null;
  }, [event, venueName]);
  const tickets = useMemo(
    () =>
      event
        ? event.ticket_types
            .filter(
              (ticket) =>
                ticket.status === "ACTIVE" || ticket.status === "SOLD_OUT"
            )
            .map((ticket) => {
              // Safety net: override FREE ticket status to SOLD_OUT when there is
              // no active credit hold or remaining credits are 0 (covers seeded
              // events and lag before events-service status propagation).
              let effectiveTicket = ticket;
              const claimableRemaining = remainingClaimableTickets(
                ticket.attributes ?? null,
                freeAvailability[ticket.id] ?? 0,
              );
              if (
                isAuthenticated &&
                isZeroPaymentClaimTicket(ticket.attributes ?? null) &&
                ticket.status === "ACTIVE" &&
                freeAvailabilityLoaded &&
                (!(ticket.id in freeAvailability) || claimableRemaining <= 0)
              ) {
                effectiveTicket = { ...ticket, status: "SOLD_OUT" };
              }

              const imageUrl = null;
              const ticketsAlreadyOwned = userTicketCounts[effectiveTicket.id] || 0;
              const formatValidTo = (validUntilIso: string) =>
                t("detail.tickets.validTo", {
                  time: formatEventDateTimeLong(
                    validUntilIso,
                    event.timezone ?? "",
                    locale === "en" ? "en" : "es"
                  ),
                });
              const payAtDoor = isPayAtDoorTicket(effectiveTicket.attributes ?? null);
              const creditRemaining = remainingClaimableTickets(
                effectiveTicket.attributes ?? null,
                freeAvailability[effectiveTicket.id] ?? 0,
              );
              const option = toDisplayOption(
                effectiveTicket,
                imageUrl,
                ticketsAlreadyOwned,
                event,
                formatValidTo,
                payAtDoor ? creditRemaining : stockAvailability[effectiveTicket.id],
                payAtDoor ? freeAvailabilityLoaded : stockAvailabilityLoaded,
                isAuthenticated,
                payAtDoor ? false : stockCheckFailed
              );
              if (option.isFree && ticketsAlreadyOwned > 0) {
                option.alreadyClaimed = true;
              }
              return option;
            })
        : [],
    [event, userTicketCounts, freeAvailability, freeAvailabilityLoaded, stockAvailability, stockAvailabilityLoaded, stockCheckFailed, isAuthenticated, t, locale]
  );

  // Use dynamic lineup from API if available, otherwise fall back to tags/sections
  const lineup = useMemo(() => {
    if (!event) {
      return [];
    }

    // If API provides lineup data, use it
    if (event.lineup && event.lineup.length > 0) {
      return event.lineup;
    }

    // Fallback: extract from tags and sections (backward compatibility)
    const unique = new Set<string>();
    const pushCandidate = (value: unknown) => {
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          unique.add(trimmed);
        }
      }
    };

    for (const tag of event.tags ?? []) {
      pushCandidate(tag);
    }

    for (const section of event.sections ?? []) {
      if (typeof section.title === "string" && section.title.toLowerCase().includes("lineup")) {
        if (Array.isArray(section.body)) {
          for (const entry of section.body as unknown[]) {
            pushCandidate(entry);
          }
        } else if (typeof section.body === "string") {
          section.body
            .split(",")
            .map((item) => item.trim())
            .forEach(pushCandidate);
        }
      }
    }

    return Array.from(unique).map((name) => ({ name, isLegacy: true as const }));
  }, [event]);

  /**
   * Compact venue map. Rendered only when the venue has GPS coordinates —
   * exactly when the productora enabled Google Maps for the event in Manager,
   * the same rule the mobile app uses. Defensive on purpose: bad coordinates
   * are a common Manager → Consumer integration issue.
   */
  const mapElement = useMemo(() => {
    if (!hasVenueCoordinates(event)) {
      return null;
    }
    return (
      <EmbeddedEventMap
        latitude={event.venue.google_latitude}
        longitude={event.venue.google_longitude}
        venueName={event.venue.name}
        directionsLabel={t("detail.location.directions")}
        mapTitle={t("detail.location.mapTitle")}
      />
    );
  }, [event, t]);

  const heroStyle = useMemo(() => {
    if (!heroImageUrl) {
      return undefined;
    }
    return {
      backgroundImage: `url(${heroImageUrl})`,
      backgroundSize: "100% 100%",
      backgroundPosition: "center",
    } as CSSProperties;
  }, [heroImageUrl]);

  const eventTitle = event.name;

  // Determine lineup display - use API lineup if available, otherwise show nothing
  const lineupDisplay = useMemo(() => {
    if (lineup.length > 0) {
      // Check if it's the new API format (array of objects)
      if (typeof lineup[0] === "object" && "type" in lineup[0]) {
        return lineup;
      }
      // Legacy format (array of strings)
      return lineup.map((item) =>
        typeof item === "string" ? { name: item, isLegacy: true as const } : item,
      );
    }
    // No lineup data - return empty array (don't show placeholder artists)
    return [];
  }, [lineup]);

  /** Social/web links to render when at least one URL is set on the event. */
  const linkItems = useMemo((): SocialLinkItem[] => {
    const links = event?.social_links;
    if (!links) return [];

    const items: SocialLinkItem[] = [];
    const instagram = links.instagram_url?.trim();
    if (instagram) {
      items.push({
        key: "instagram",
        href: instagram,
        label: tCommon("socialPlatforms.instagram"),
        displayValue: formatSocialLinkDisplayValue("instagram", instagram),
        icon: <InstagramLinkIcon />,
      });
    }
    const twitter = links.twitter_url?.trim();
    if (twitter) {
      items.push({
        key: "twitter",
        href: twitter,
        label: tCommon("socialPlatforms.twitter"),
        displayValue: formatSocialLinkDisplayValue("twitter", twitter),
        icon: <TwitterLinkIcon />,
      });
    }
    const website = links.website_url?.trim();
    if (website) {
      items.push({
        key: "website",
        href: website,
        label: t("detail.links.website"),
        displayValue: formatSocialLinkDisplayValue("website", website, {
          websiteDisplayLabel: links.website_display_label,
        }),
        icon: <WebsiteLinkIcon />,
      });
    }
    return items;
  }, [event?.social_links, t, tCommon]);

  return (
    <article className="gruv-event">
      <section className="gruv-event__hero gruv-anim-up" style={heroStyle} aria-label="">
        <span className="gruv-event__hero-glow" aria-hidden="true" />
      </section>

      <section className="gruv-event__body">
        <header className="gruv-event__heading gruv-anim-up" style={{ ["--gruv-anim-delay" as string]: "60ms" }}>
          <h1 className="gruv-event__page-title" data-testid="event-page-title">{eventTitle}</h1>
          <EventMetaRow items={metaItems} ariaLabel={t("detail.meta.aria")} />
        </header>
        <div className="gruv-event__columns" data-testid="event-page-columns">
        <div className="gruv-event__details gruv-stagger">
          <div className="gruv-event__details-card" data-testid="event-page-about">
            <h2>{t("detail.about.title")}</h2>
            <CollapsibleDescription
              text={event?.description ?? t("detail.about.fallback")}
              readMoreLabel={t("detail.about.readMore")}
              readLessLabel={t("detail.about.readLess")}
            />
          </div>

          {/* Ubicación: "Ubicación: Venue" + street line, then the compact map (GPS only) */}
          {event?.venue && (venueName || addressLine || mapElement) ? (
            <div className="gruv-event__details-card gruv-event__location" data-testid="event-location">
              {venueMapsUrl ? (
                <a
                  href={venueMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gruv-event__location-head"
                  aria-label={t("detail.location.openMaps")}
                >
                  <h2 data-testid="event-location-title">
                    {t("detail.location.title")}
                    {venueName ? <span className="gruv-event__location-venue">{`: ${venueName}`}</span> : null}
                  </h2>
                  {addressLine ? (
                    <p className="gruv-event__location-address" data-testid="event-location-address">{addressLine}</p>
                  ) : null}
                </a>
              ) : (
                <div className="gruv-event__location-head">
                  <h2 data-testid="event-location-title">
                    {t("detail.location.title")}
                    {venueName ? <span className="gruv-event__location-venue">{`: ${venueName}`}</span> : null}
                  </h2>
                  {addressLine ? (
                    <p className="gruv-event__location-address" data-testid="event-location-address">{addressLine}</p>
                  ) : null}
                </div>
              )}
              {mapElement}
            </div>
          ) : null}
          {/* Social / web links — shown when at least one URL is set */}
          {linkItems.length > 0 && (
            <div className="gruv-event__details-card">
              <h2>{t("detail.links.title")}</h2>
              <div className="gruv-social-chips gruv-stagger">
                {linkItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className={`gruv-social-chip gruv-social-chip--${item.key}`}
                  >
                    <span className="gruv-social-chip__icon">{item.icon}</span>
                    <span className="gruv-social-chip__text">
                      <span className="gruv-social-chip__label">{item.label}</span>
                      <span className="gruv-social-chip__value">{item.displayValue}</span>
                    </span>
                    <ExternalArrowIcon className="gruv-social-chip__arrow" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {/* Lineup Section - Only show if there are artists */}
          {lineupDisplay.length > 0 && (
            <div className="gruv-event__lineup-card">
              <h2>{t("detail.lineup.title")}</h2>
              <div
                className="gruv-lineup gruv-stagger"
                data-count={Math.min(lineupDisplay.length, 5)}
              >
                {lineupDisplay.map((item, index) => {
                  const isLegacy = "isLegacy" in item;
                  const name = item.name;
                  const imageUrl = isLegacy ? null : item.image_url;
                  const url = isLegacy ? `https://open.spotify.com/search/${encodeURIComponent(name)}` : item.url;
                  const linkText = t("detail.lineup.listenSpotify");
                  const key = isLegacy ? `legacy-${index}-${name}` : `${item.type}-${item.id}`;

                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="gruv-artist-card"
                      aria-label={`${name} · ${linkText}`}
                    >
                      <span className="gruv-artist-card__avatar">
                        {imageUrl ? (
                          <img src={imageUrl} alt="" />
                        ) : (
                          <span aria-hidden="true">{name.charAt(0).toUpperCase()}</span>
                        )}
                        <span className="gruv-artist-card__badge" aria-hidden="true">
                          <SpotifyGlyph />
                        </span>
                      </span>
                      <span className="gruv-artist-card__name" title={name}>{name}</span>
                      <ExternalArrowIcon className="gruv-artist-card__arrow" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Video Preview Section */}
          {event?.youtube_video && event.youtube_video.visible && (
            <div className="gruv-event__video-preview-card mt-6">
              <h2>{t("detail.video.title")}</h2>
              <div
                className="gruv-event__video-container"
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  maxWidth: "100%",
                  marginTop: "1rem"
                }}
              >
                <iframe
                  src={event.youtube_video.embed_url}
                  title={event.youtube_video.title}
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                />
              </div>
              <div className="gruv-event__video-info mt-3">
                <div className="font-medium">{event.youtube_video.title}</div>
                <div className="text-sm text-gray-500">{event.youtube_video.channel_title}</div>
              </div>
            </div>
          )}
        </div>

        <aside className="gruv-event__sidebar gruv-anim-up" style={{ ["--gruv-anim-delay" as string]: "120ms" }} data-testid="event-page-tickets">
          <div className="gruv-event__tickets-card">
            <header className="gruv-event__tickets-header">
              <span className="gruv-event__tickets-kicker" aria-hidden="true" />
              <h2>{t("detail.tickets.title")}</h2>
              <p>{t("detail.tickets.subtitle")}</p>
            </header>
            {event && !isLoading && isAuthenticated ? (
              <div className="gruv-event__friend-ticket-slot">
                <FriendTicketCount eventId={event.id} />
              </div>
            ) : null}
            {tickets.length > 0 ? (
              <TicketSelectionClient
                eventId={event.id}
                eventSlug={slug}
                eventName={eventTitle}
                tickets={tickets}
                productoraId={event.productora_id ?? null}
                promoterInUrl={!!promoterCode}
                purchaseAvailable={event.purchase_available !== false}
                purchaseBlockReason={event.purchase_block_reason}
                eventUrlQuery={searchParams.toString()}
                onStockRetry={retryStockAvailability}
              />
            ) : event.ticket_types.length > 0 ? (
              <div className="gruv-event__ticket-selection">
                <p className="gruv-event__hint">{t("detail.tickets.preSaleSoon")}</p>
              </div>
            ) : (
              <div className="gruv-event__ticket-selection">
                <p className="gruv-event__hint">{t("detail.tickets.empty")}</p>
              </div>
            )}
          </div>
          <div className="gruv-event__nav">
            <Link href="/events">
              <Button variant="secondary" className="gruv-event__nav-button">
                {t("detail.nav.backToList")}
              </Button>
            </Link>
            <Link href="/events">
              <Button variant="ghost" className="gruv-event__nav-button">
                {t("detail.nav.backHome")}
              </Button>
            </Link>
          </div>
        </aside>
        </div>
      </section>
    </article>
  );
};
